import { useEffect, useState } from "react";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  CalendarRange,
  Flame,
  Globe2,
  MapPin,
  Megaphone,
  Rocket,
  Search,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { fetchPublicStats } from "../statsApi.ts";
import type {
  EventSourcesBreakdown,
  NameCount,
  PublicStats,
  UnmetDemandItem,
} from "../../../src/publicStats.ts";
import { Label, Select } from "../components/ui/index.ts";

const cardStyle = "bg-white/5 border border-white/10 rounded-xl";

const sectionTitleStyle =
  "flex items-center gap-2 text-white font-semibold text-base border-b border-white/10 pb-3";

const formatNumber = (value: number) => value.toLocaleString("en-US");

const countries = [
  { value: "", label: "All Countries" },
  { value: "Israel", label: "Israel" },
  { value: "US", label: "US" },
  { value: "Germany", label: "Germany" },
  { value: "Argentina", label: "Argentina" },
];

const StatCard = ({
  icon,
  value,
  label,
  delay,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  delay: string;
}) => (
  <div
    className={`${cardStyle} p-5 animate-fade-up`}
    style={{ animationDelay: delay }}
  >
    <div className="flex items-center gap-2 text-purple-400 mb-2">{icon}</div>
    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
      {value}
    </div>
    <div className="text-xs text-gray-400">{label}</div>
  </div>
);

const BarList = ({
  title,
  icon,
  rows,
  barClass,
  emptyText,
}: {
  title: string;
  icon: React.ReactNode;
  rows: NameCount[];
  barClass: string;
  emptyText?: string;
}) => {
  const max = Math.max(1, ...rows.map(({ count }) => count));
  return (
    <div className={`${cardStyle} p-6 space-y-4`}>
      <div className={sectionTitleStyle}>
        {icon}
        {title}
      </div>
      {rows.length === 0
        ? (
          <p className="text-sm text-gray-500 italic">
            {emptyText ?? "No events recorded."}
          </p>
        )
        : (
          <div className="space-y-2.5">
            {rows.map(({ name, count }, i) => (
              <div key={name} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-300 capitalize truncate pr-2">
                    {name}
                  </span>
                  <span className="text-gray-400 font-medium shrink-0">
                    {formatNumber(count)}
                  </span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barClass} transition-all duration-700 ease-out`}
                    style={{
                      width: `${Math.max(4, (count / max) * 100)}%`,
                      transitionDelay: `${i * 40}ms`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

const donutSegments = (
  parts: { value: number; color: string; label: string }[],
) => {
  const total = Math.max(
    1,
    parts.reduce((acc, { value }) => acc + value, 0),
  );
  const circumference = 2 * Math.PI * 42;
  let offset = 0;
  return parts.map(({ value, color, label }) => {
    const fraction = value / total;
    const segment = {
      label,
      color,
      dasharray: `${fraction * circumference} ${circumference}`,
      dashoffset: -offset * circumference,
      fraction,
    };
    offset += fraction;
    return segment;
  });
};

const SourceDonut = ({
  sources,
  userSubmitted,
  apiSubmitted,
  scraped,
}: {
  sources?: EventSourcesBreakdown;
  userSubmitted: number;
  apiSubmitted: number;
  scraped: number;
}) => {
  const parts = sources
    ? [
      {
        value: sources.publicWeb,
        color: "#a855f7",
        label: "Public Web Pages",
      },
      {
        value: sources.whatsappGroups,
        color: "#22c55e",
        label: "WhatsApp Groups",
      },
      {
        value: sources.instagram,
        color: "#ec4899",
        label: "Instagram",
      },
      {
        value: sources.facebookGroups,
        color: "#3b82f6",
        label: "Facebook Groups",
      },
      {
        value: sources.community,
        color: "#38bdf8",
        label: "Community Submissions",
      },
    ].filter(({ value }) => value > 0)
    : [
      { value: scraped, color: "#a855f7", label: "Public Web Pages" },
      {
        value: userSubmitted,
        color: "#38bdf8",
        label: "Community Submissions",
      },
      { value: apiSubmitted, color: "#ec4899", label: "Instagram" },
    ];
  return (
    <div className={`${cardStyle} p-6 space-y-4`}>
      <div className={sectionTitleStyle}>
        <BarChart3 size={18} className="text-purple-400" />
        Where events come from
      </div>
      {parts.length === 0
        ? <p className="text-sm text-gray-500 italic">No events recorded.</p>
        : (
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
            <svg
              viewBox="0 0 100 100"
              className="w-32 h-32 sm:w-36 sm:h-36 -rotate-90 shrink-0"
            >
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="12"
              />
              {donutSegments(parts).map((segment) => (
                <circle
                  key={segment.label}
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={segment.color}
                  strokeWidth="12"
                  strokeDasharray={segment.dasharray}
                  strokeDashoffset={segment.dashoffset}
                  strokeLinecap="butt"
                />
              ))}
            </svg>
            <div className="space-y-2.5 text-sm w-full">
              {parts.map(({ color, label, value }) => (
                <div key={label} className="flex items-center gap-2.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: color }}
                  />
                  <span className="text-gray-300 text-xs sm:text-sm truncate">
                    {label}
                  </span>
                  <span className="text-white font-medium ml-auto pl-2 text-xs sm:text-sm font-mono shrink-0">
                    {formatNumber(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

const UnmetDemandSection = ({ items }: { items: UnmetDemandItem[] }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={`${cardStyle} p-6 sm:p-8 space-y-5 animate-fade-up`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center gap-2 text-white font-bold text-lg">
            <Flame size={20} className="text-amber-400" />
            Unmet Demand & Producer Opportunities
          </div>
          <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
            Active user searches with zero or few upcoming events. High
            opportunity for producers to promote and fill attendance.
          </p>
        </div>
        <span className="text-xs text-amber-300 font-medium px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 self-start sm:self-auto shrink-0">
          High Search Deficit
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div
            key={item.name}
            className="bg-black/40 border border-white/10 hover:border-purple-500/40 rounded-xl p-4 flex flex-col justify-between space-y-3 transition group"
          >
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white capitalize group-hover:text-purple-300 transition truncate">
                  {item.name}
                </span>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                    item.upcomingCount === 0
                      ? "bg-red-500/20 text-red-300 border border-red-500/30"
                      : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                  }`}
                >
                  {item.upcomingCount === 0
                    ? "0 events live"
                    : `${item.upcomingCount} event${
                      item.upcomingCount === 1 ? "" : "s"
                    }`}
                </span>
              </div>
              <div className="text-xs text-gray-400 flex items-center gap-1.5">
                <Search size={13} className="text-purple-400 shrink-0" />
                <span className="font-semibold text-white">
                  {formatNumber(item.searchesCount)}
                </span>{" "}
                searches looking for this
              </div>
            </div>

            <Link
              to={`/promote?${
                item.kind === "location"
                  ? `area=${encodeURIComponent(item.name)}`
                  : `category=${encodeURIComponent(item.name.toLowerCase())}`
              }`}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-purple-600/30 border border-white/10 hover:border-purple-500/40 text-xs font-medium text-purple-300 hover:text-white transition"
            >
              Fill this demand <ArrowRight size={13} />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export const StatsPageContent = () => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setStats(null);
    setFailed(false);
    fetchPublicStats({
      ...(selectedCountry ? { country: selectedCountry } : {}),
    })
      .then(setStats)
      .catch(() => setFailed(true));
  }, [selectedCountry]);

  const activeCountryLabel = countries.find((c) => c.value === selectedCountry)
    ?.label ?? "All Countries";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white">
      <div className="container mx-auto px-4 py-10 max-w-5xl space-y-8">
        {/* Header with Country Switcher */}
        <header className="animate-fade-up flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              What people are going out for
            </h1>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl leading-relaxed">
              A live pulse of the underground and event scene: active users,
              upcoming events, and what everyone is searching for.
            </p>
          </div>

          {/* Country Selector */}
          <div className="shrink-0 flex items-center gap-2 self-start sm:self-auto">
            <Label
              htmlFor="stats-country-selector"
              icon={<Globe2 size={14} className="text-purple-400" />}
            >
              Country
            </Label>
            <Select
              id="stats-country-selector"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.currentTarget.value)}
              options={countries}
            />
          </div>
        </header>

        {/* Overview Metric Cards */}
        {!stats && !failed && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`${cardStyle} p-5 animate-pulse`}>
                <div className="h-4 w-8 bg-white/10 rounded mb-3" />
                <div className="h-8 w-20 bg-white/10 rounded mb-2" />
                <div className="h-3 w-24 bg-white/5 rounded" />
              </div>
            ))}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Users size={18} />}
              value={formatNumber(stats.overview.totalUsers)}
              label={selectedCountry
                ? `Community users (${activeCountryLabel})`
                : "Community users"}
              delay="0ms"
            />
            <StatCard
              icon={<Activity size={18} />}
              value={formatNumber(stats.overview.active7d)}
              label={selectedCountry
                ? `Active this week (${activeCountryLabel})`
                : "Active this week"}
              delay="80ms"
            />
            <StatCard
              icon={<CalendarRange size={18} />}
              value={formatNumber(stats.overview.totalUpcomingEvents)}
              label={selectedCountry
                ? `Upcoming events (${activeCountryLabel})`
                : "Upcoming events"}
              delay="160ms"
            />
            <StatCard
              icon={<Search size={18} />}
              value={formatNumber(stats.overview.totalSearches30d)}
              label={selectedCountry
                ? `Searches 30d (${activeCountryLabel})`
                : "Searches (last 30 days)"}
              delay="240ms"
            />
          </div>
        )}

        {failed && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            Failed to load live stats. Please try again later.
          </div>
        )}

        {/* Breakdown Charts (2x2 Grid) */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-in">
            <BarList
              title="Top event categories"
              icon={<BarChart3 size={18} className="text-purple-400" />}
              rows={stats.events.byCategory}
              barClass="bg-purple-600"
              emptyText="No categories recorded."
            />
            <BarList
              title="Top cities"
              icon={<MapPin size={18} className="text-purple-400" />}
              rows={stats.events.byCity}
              barClass="bg-purple-500"
              emptyText="No cities recorded."
            />
            <SourceDonut
              sources={stats.events.sources}
              userSubmitted={stats.events.userSubmitted}
              apiSubmitted={stats.events.apiSubmitted}
              scraped={stats.events.scraped}
            />
            <BarList
              title="What people are searching for"
              icon={<Search size={18} className="text-purple-400" />}
              rows={stats.searches.topCategories}
              barClass="bg-purple-500"
              emptyText="No searches recorded."
            />
          </div>
        )}

        {/* Unmet Demand Section */}
        {stats && stats.unmetDemand && (
          <UnmetDemandSection items={stats.unmetDemand} />
        )}

        {/* Promote CTA Banner */}
        <div className="bg-white/5 border border-purple-500/30 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-white font-bold text-lg">
              <Megaphone size={20} className="text-purple-400" />
              Organizing an event?
            </div>
            <p className="text-gray-400 text-sm max-w-xl leading-relaxed">
              Push your event directly into the WhatsApp chats of matching
              subscribers. Target by genre, city, age, and gender.
            </p>
          </div>
          <Link
            to="/promote"
            className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold transition shadow-lg shadow-purple-600/25 shrink-0 flex items-center gap-2"
          >
            <Rocket size={16} />
            Promote an Event
          </Link>
        </div>

        <footer className="pt-6 text-center text-xs text-gray-500">
          Aggregated and anonymized.{" "}
          <Link to="/events" className="text-purple-400 hover:text-purple-300">
            Open Agent FOMO
          </Link>
        </footer>
      </div>
    </div>
  );
};
